import { FC, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { ToggleButton, ToggleButtonGroup, FormControl, Typography } from '@mui/material';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';

const StyledFormControl = styled(FormControl)<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .label {
    color: ${props => props.$colorMode === 'dark' ? Colors.grey1 : Colors.grey9};
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 12px;
    margin-left: 4px;
    margin-bottom: 2px;
  }
`;

interface StyledGroupProps {
  $colorMode: 'light' | 'dark';
  orientation: 'horizontal' | 'vertical';
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)<StyledGroupProps>`
  background-color: ${props => props.$colorMode === 'dark' ? Colors.grey9 : Colors.background};
  border-radius: 4px;
  display: flex;
  flex-direction: ${props => props.orientation === 'vertical' ? 'column' : 'row'};
  border: 1px solid ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1};
  overflow: hidden;

  & .MuiToggleButtonGroup-grouped {
    border: none;

    ${props => props.orientation === 'vertical' ? css<StyledGroupProps>`
      &:not(:first-of-type) {
        border-top: 1px solid ${p => p.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1};
      }
    ` : css<StyledGroupProps>`
      &:not(:first-of-type) {
        border-left: 1px solid ${p => p.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1};
      }
    `}

    &.Mui-disabled {
      opacity: 0.5;
    }
  }
`;

const StyledToggleButton = styled(ToggleButton)<{ $colorMode: 'light' | 'dark'; $orientation: 'horizontal' | 'vertical' }>`
  color: ${props => props.$colorMode === 'dark' ? Colors.grey2 : Colors.grey8};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 12px;
  line-height: 1.4em;
  text-transform: none;
  padding: 4px 8px;
  flex-grow: 1;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: ${props => props.$orientation === 'vertical' ? 'row' : 'column'};
  align-items: center;
  justify-content: ${props => props.$orientation === 'vertical' ? 'space-between' : 'center'};
  gap: ${props => props.$orientation === 'vertical' ? '8px' : '0px'};
  border-radius: 0;
  
  /* Reset MUI default border to avoid conflict */
  border: none !important;

  &:hover {
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1};
    /* Use inset box shadow instead of border to prevent alignment issues with the container */
    box-shadow: inset 0 0 0 1px ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
    z-index: 2;
  }

  &.Mui-selected {
    color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black} !important;
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1} !important;
    box-shadow: inset 0 0 0 1px ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
    z-index: 1;
  }
`;

const SubText = styled.span<{ $colorMode: 'light' | 'dark'; $selected: boolean }>`
  font-size: 10px;
  opacity: 0.7;
  color: ${props => {
    if (props.$selected) return props.$colorMode === 'dark' ? Colors.white : Colors.black;
    return props.$colorMode === 'dark' ? Colors.grey4 : Colors.grey6;
  }};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  line-height: 1.4em;
`;

export interface SelectButtonOption {
  id: string;
  value: string;
  subValue?: string;
}

export interface SelectButtonGroupProps {
  label?: string;
  value: string;
  options: SelectButtonOption[];
  onChange: (newValue: string) => void;
  colorMode?: 'light' | 'dark';
  width?: string | number;
  disabled?: boolean;
  exclusive?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

const SelectButtonGroup: FC<SelectButtonGroupProps> = ({
  label,
  value,
  options,
  onChange,
  colorMode = 'dark',
  width = '100%',
  disabled = false,
  exclusive = true,
  orientation = 'horizontal',
}) => {
  const handleFormat = (
    _event: MouseEvent<HTMLElement>,
    newValue: string | null,
  ) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <StyledFormControl 
      $colorMode={colorMode} 
      style={{ width }}
      disabled={disabled}
    >
      {label && <Typography className="label">{label}</Typography>}
      <StyledToggleButtonGroup
        value={value}
        exclusive={exclusive}
        onChange={handleFormat}
        $colorMode={colorMode}
        orientation={orientation}
        size="small"
        fullWidth
      >
        {options.map((option) => (
          <StyledToggleButton
            key={option.id}
            value={option.id}
            $colorMode={colorMode}
            $orientation={orientation}
            disableRipple
          >
            <div>{option.value}</div>
            {option.subValue && (
              <SubText $colorMode={colorMode} $selected={value === option.id}>
                {option.subValue}
              </SubText>
            )}
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
    </StyledFormControl>
  );
};

export default SelectButtonGroup;